/**
 * Performance & Battery settings.
 * 
 * Three modes:
 * - "high"          → Full 60fps animations, all effects, full blur
 * - "balanced"      → Reduced fps, fewer particles, no blur/gradients (default for mobile)
 * - "battery-saver" → All animations OFF, static colors, no canvas effects
 * 
 * Anti-cheat: settings are HMAC-signed so that localStorage tampering is detected.
 */

import { safeSetItem } from '@/lib/safeStorage';

export type PerformanceMode = 'high' | 'balanced';

const PERF_MODE_KEY = 'aphenis_performance_mode';
const BATTERY_SAVER_KEY = 'aphenis_battery_saver';
const PERF_SIG_KEY = 'aphenis_perf_sig';

// ─── HMAC Integrity ───

function getIntegritySecret(): string {
  try {
    const raw = localStorage.getItem('vaultmsg_uid');
    if (raw) {
      const identity = JSON.parse(raw);
      if (identity.signingPublicKey) return identity.signingPublicKey.slice(0, 32);
    }
  } catch {}
  return 'aphenis_perf_integrity_seed_v1';
}

async function computePerfHMAC(): Promise<string> {
  const mode = localStorage.getItem(PERF_MODE_KEY) || '';
  const battery = localStorage.getItem(BATTERY_SAVER_KEY) || '';
  const payload = `perf:${mode}|bat:${battery}`;
  const secret = getIntegritySecret();
  const encoder = new TextEncoder();
  try {
    const key = await crypto.subtle.importKey(
      'raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
    );
    const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(payload));
    return Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 32);
  } catch { return ''; }
}

async function signPerfSettings(): Promise<void> {
  const sig = await computePerfHMAC();
  if (sig) safeSetItem(PERF_SIG_KEY, sig);
}

export async function verifyPerfIntegrity(): Promise<boolean> {
  const storedSig = localStorage.getItem(PERF_SIG_KEY);
  if (!storedSig) {
    await signPerfSettings();
    return true;
  }
  const expected = await computePerfHMAC();
  if (!expected) return true;
  return expected === storedSig;
}

export async function resealPerfSettings(): Promise<void> {
  await signPerfSettings();
}

// ─── Getters ───

function isDesktop(): boolean {
  return window.innerWidth >= 768;
}

export function getPerformanceMode(): PerformanceMode {
  if (isDesktop()) return 'high';
  const stored = localStorage.getItem(PERF_MODE_KEY);
  if (stored === 'high' || stored === 'balanced') return stored;
  return 'balanced';
}

export function isBatterySaver(): boolean {
  if (isDesktop()) return false;
  return localStorage.getItem(BATTERY_SAVER_KEY) === 'true';
}

export function isBalancedOrLower(): boolean {
  if (isDesktop()) return false;
  if (isBatterySaver()) return true;
  return getPerformanceMode() === 'balanced';
}

export function shouldAnimate(): boolean {
  if (isDesktop()) return true;
  if (isBatterySaver()) return false;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  return true;
}

export function shouldUseBlur(): boolean {
  if (isDesktop()) return true;
  if (isBatterySaver()) return false;
  return getPerformanceMode() === 'high';
}

export function shouldUseGradientParticles(): boolean {
  if (isDesktop()) return true;
  if (isBatterySaver()) return false;
  return getPerformanceMode() === 'high';
}

export function getFrameSkip(): number {
  if (isDesktop()) return 1;
  if (isBatterySaver()) return Infinity;
  return getPerformanceMode() === 'high' ? 1 : 4;
}

export function getParticleDensity(): number {
  if (isDesktop()) return 1;
  if (isBatterySaver()) return 0;
  return getPerformanceMode() === 'high' ? 1 : 0.4;
}

// ─── Setters ───

export function setPerformanceMode(mode: PerformanceMode): void {
  safeSetItem(PERF_MODE_KEY, mode);
  applyPerformanceClass();
  signPerfSettings().catch(() => {});
}

export function setBatterySaver(enabled: boolean): void {
  safeSetItem(BATTERY_SAVER_KEY, enabled ? 'true' : 'false');
  applyPerformanceClass();
  signPerfSettings().catch(() => {});
}

// ─── DOM class application ───

export function applyPerformanceClass(): void {
  const html = document.documentElement;
  html.classList.remove('battery-saver', 'perf-balanced', 'perf-high');
  if (isBatterySaver()) {
    html.classList.add('battery-saver');
  } else {
    html.classList.add(`perf-${getPerformanceMode()}`);
  }
}

export function initPerformanceSettings(): void {
  applyPerformanceClass();
}
